import type {
  AlertRow,
  AlertType,
  EngagementAssessment,
  EngagementSnapshot,
} from '../types';
import { riskTierForScore } from '../lib/engagement-health';

/**
 * Synthetic engagement-health alerts so the SPA renders convincingly in mocks
 * mode. Mix of the four alert types; one opportunity (O15411) trips two rules
 * to exercise the "same opp, two rows" UI path.
 */
type LegacyMockAlert = Omit<
  AlertRow,
  | 'engagementStatus'
  | 'accountRollupName'
  | 'accountDisplayName'
  | 'ownerId'
  | 'ownerEmail'
  | 'csmId'
  | 'csmName'
  | 'csmEmail'
  | 'projectNumbers'
  | 'coacheeName'
>;

const MOCK_ALERTS: LegacyMockAlert[] = [
  // ── Stalled starts (4) ────────────────────────────────────────────────
  {
    id: 'a01',
    opportunityId: '0061T00000Stalled1',
    opportunityName: 'Tate Murakami - C-Suite Coaching',
    accountName: 'Lockheed Martin',
    ownerName: 'Tia Kowalczyk',
    sowStartDate: '2026-04-15',
    sowEndDate: '2026-12-15',
    contractedHours: 40,
    loggedHours: 0,
    pctComplete: 0,
    lastSessionDate: null,
    daysSinceLastSession: null,
    daysSinceStart: 35,
    deliveringAffiliates: [{ name: 'Bernice Ang', email: 'bernice.ang@arielexample.com' }],
    alertType: 'stalled_start',
    snooze: null,
  },
  {
    id: 'a02',
    opportunityId: '0061T00000Stalled2',
    opportunityName: 'Robin Park - Onboarding Cohort',
    accountName: 'Salesforce',
    ownerName: 'Jermaine Castillo',
    sowStartDate: '2026-04-28',
    sowEndDate: '2026-11-28',
    contractedHours: 24,
    loggedHours: 0,
    pctComplete: 0,
    lastSessionDate: null,
    daysSinceLastSession: null,
    daysSinceStart: 22,
    deliveringAffiliates: [{ name: 'Cynthia Cobb', email: 'cynthia.cobb@arielexample.com' }],
    alertType: 'stalled_start',
    snooze: null,
  },
  {
    id: 'a03',
    opportunityId: '0061T00000Stalled3',
    opportunityName: 'Edith Yamashita - Executive Coaching',
    accountName: 'Pfizer Global',
    ownerName: 'Tia Kowalczyk',
    sowStartDate: '2026-05-01',
    sowEndDate: '2027-01-01',
    contractedHours: 36,
    loggedHours: 0,
    pctComplete: 0,
    lastSessionDate: null,
    daysSinceLastSession: null,
    daysSinceStart: 19,
    deliveringAffiliates: [{ name: 'Eric Leininger', email: 'eric.leininger@arielexample.com' }],
    alertType: 'stalled_start',
    snooze: null,
  },
  {
    id: 'a04',
    opportunityId: '0061T00000Stalled4',
    opportunityName: 'Casey Levkov - Sponsorship Coaching',
    accountName: 'JPMorgan Chase',
    ownerName: 'Devon Ricci',
    sowStartDate: '2026-04-20',
    sowEndDate: '2026-10-20',
    contractedHours: 32,
    loggedHours: 0,
    pctComplete: 0,
    lastSessionDate: null,
    daysSinceLastSession: null,
    daysSinceStart: 30,
    deliveringAffiliates: [{ name: 'Diane Holcomb', email: 'diane.holcomb@arielexample.com' }],
    alertType: 'stalled_start',
    snooze: { snoozedUntil: '2026-12-31T00:00:00Z', snoozedByEmail: 'leah@arielgroup.com' },
  },

  // ── Near completion (3) ───────────────────────────────────────────────
  {
    id: 'a05',
    opportunityId: '0061T00000Near001',
    opportunityName: 'Wes Sprague - Coaching Extension',
    accountName: 'Illinois Tool Works Inc.',
    ownerName: 'Cynthia Cobb',
    sowStartDate: '2026-04-13',
    sowEndDate: '2026-08-13',
    contractedHours: 13,
    loggedHours: 11.5,
    pctComplete: 0.885,
    lastSessionDate: '2026-05-15',
    daysSinceLastSession: 5,
    daysSinceStart: 37,
    deliveringAffiliates: [{ name: 'Hugh Marriott', email: null }],
    alertType: 'near_completion',
    snooze: null,
  },
  {
    id: 'a06',
    opportunityId: '0061T00000Near002',
    opportunityName: 'Jacob Liu - Custom VILT Cohort',
    accountName: 'Bank of America',
    ownerName: 'Aaron Klemensky',
    sowStartDate: '2025-09-01',
    sowEndDate: '2026-08-31',
    contractedHours: 80,
    loggedHours: 67,
    pctComplete: 0.838,
    lastSessionDate: '2026-05-14',
    daysSinceLastSession: 6,
    daysSinceStart: 261,
    deliveringAffiliates: [{ name: 'Aaron Klemensky', email: 'aaron.k@arielexample.com' }, { name: 'Joanne Pearce', email: null }],
    alertType: 'near_completion',
    snooze: null,
  },
  {
    id: 'a07',
    opportunityId: '0061T00000Near003',
    opportunityName: 'Priya Ramachandran - Foundations',
    accountName: 'AbbVie',
    ownerName: 'Diane Holcomb',
    sowStartDate: '2026-01-08',
    sowEndDate: '2026-09-08',
    contractedHours: 64,
    loggedHours: 53,
    pctComplete: 0.828,
    lastSessionDate: '2026-05-12',
    daysSinceLastSession: 8,
    daysSinceStart: 132,
    deliveringAffiliates: [{ name: 'Marisol Bahena', email: 'marisol.bahena@arielexample.com' }],
    alertType: 'near_completion',
    snooze: null,
  },

  // ── Stale activity (4) ────────────────────────────────────────────────
  {
    id: 'a08',
    opportunityId: '0061T00000Stale01',
    opportunityName: 'Hannah Goldfarb - Targeted Coaching',
    accountName: 'United Therapeutics',
    ownerName: 'Sophie Marchetti',
    sowStartDate: '2026-02-24',
    sowEndDate: '2026-11-24',
    contractedHours: 22,
    loggedHours: 9.5,
    pctComplete: 0.432,
    lastSessionDate: '2026-04-22',
    daysSinceLastSession: 28,
    daysSinceStart: 85,
    deliveringAffiliates: [{ name: 'Tomasz Ruda', email: 'tomasz.ruda@arielexample.com' }],
    alertType: 'stale_activity',
    snooze: null,
  },
  {
    id: 'a09',
    opportunityId: '0061T00000Stale02',
    opportunityName: 'Lia Brennan - Targeted Coaching',
    accountName: 'Mastercard International',
    ownerName: 'Jamal Burch',
    sowStartDate: '2025-10-20',
    sowEndDate: '2026-10-20',
    contractedHours: 45,
    loggedHours: 27,
    pctComplete: 0.6,
    lastSessionDate: '2026-04-23',
    daysSinceLastSession: 27,
    daysSinceStart: 212,
    deliveringAffiliates: [{ name: 'Helen Whitcomb', email: 'helen.whitcomb@arielexample.com' }],
    alertType: 'stale_activity',
    snooze: null,
  },
  // Same opp (O15411) appears in two alert lists: near_completion + stale_activity
  {
    id: 'a10',
    opportunityId: '0061T00000DoubleHit',
    opportunityName: 'Dana Walz - Open Enrollment',
    accountName: 'AbbVie',
    ownerName: 'Hugh Marriott',
    sowStartDate: '2025-11-04',
    sowEndDate: '2026-07-04',
    contractedHours: 40,
    loggedHours: 34,
    pctComplete: 0.85,
    lastSessionDate: '2026-04-20',
    daysSinceLastSession: 30,
    daysSinceStart: 197,
    deliveringAffiliates: [{ name: 'Jamal Burch', email: 'jamal.burch@arielexample.com' }],
    alertType: 'near_completion',
    snooze: null,
  },
  {
    id: 'a11',
    opportunityId: '0061T00000DoubleHit',
    opportunityName: 'Dana Walz - Open Enrollment',
    accountName: 'AbbVie',
    ownerName: 'Hugh Marriott',
    sowStartDate: '2025-11-04',
    sowEndDate: '2026-07-04',
    contractedHours: 40,
    loggedHours: 34,
    pctComplete: 0.85,
    lastSessionDate: '2026-04-20',
    daysSinceLastSession: 30,
    daysSinceStart: 197,
    deliveringAffiliates: [{ name: 'Sophie Marchetti', email: 'sophie.marchetti@arielexample.com' }],
    alertType: 'stale_activity',
    snooze: null,
  },
  {
    id: 'a12',
    opportunityId: '0061T00000Stale04',
    opportunityName: 'Robert Kim - Communication Coaching',
    accountName: 'Salesforce',
    ownerName: 'Tomasz Ruda',
    sowStartDate: '2025-12-01',
    sowEndDate: '2026-11-30',
    contractedHours: 36,
    loggedHours: 14,
    pctComplete: 0.389,
    lastSessionDate: '2026-04-19',
    daysSinceLastSession: 31,
    daysSinceStart: 170,
    deliveringAffiliates: [{ name: 'Devon Ricci', email: 'devon.ricci@arielexample.com' }],
    alertType: 'stale_activity',
    snooze: null,
  },
];

export const ENGAGEMENT_ALERTS: AlertRow[] = MOCK_ALERTS.map((alert) => ({
  ...alert,
  accountRollupName: alert.accountName === 'Pfizer Global' ? 'Pfizer' : null,
  accountDisplayName: alert.accountName === 'Pfizer Global' ? 'Pfizer' : alert.accountName,
  ownerId: `owner-${alert.ownerName?.toLowerCase().replace(/\s+/g, '-') ?? 'unknown'}`,
  ownerEmail: alert.ownerName ? `${alert.ownerName.toLowerCase().replace(/\s+/g, '.')}@arielexample.com` : null,
  csmId: 'csm-christine-lane',
  csmName: 'Christine Lane',
  csmEmail: 'christine.lane@arielexample.com',
  projectNumbers: [`P-${alert.id.toUpperCase()}`],
  coacheeName: alert.opportunityName?.split(' - ')[0] ?? null,
  engagementStatus: 'Active',
}));

const MOCK_POINTS: Record<AlertType, number> = {
  stalled_start: 35,
  near_completion: 15,
  stale_activity: 30,
  stale_activity_30d: 30,
};

const byOpportunity = new Map<string, AlertRow[]>();
for (const alert of ENGAGEMENT_ALERTS) {
  const rows = byOpportunity.get(alert.opportunityId) ?? [];
  rows.push(alert);
  byOpportunity.set(alert.opportunityId, rows);
}

export const ENGAGEMENT_ASSESSMENTS: EngagementAssessment[] = [
  ...Array.from(byOpportunity.values()).map((rows) => {
    const first = rows[0];
    const signalTypes = [...new Set(rows.map((row) => row.alertType))];
    const riskSignals = signalTypes.map((type) => ({
      type,
      points: MOCK_POINTS[type],
      detail: { points: MOCK_POINTS[type] },
    }));
    const riskScore = Math.min(100, riskSignals.reduce((sum, signal) => sum + signal.points, 0));
    return {
      opportunityId: first.opportunityId,
      opportunityName: first.opportunityName,
      accountName: first.accountName,
      accountRollupName: first.accountRollupName,
      accountDisplayName: first.accountDisplayName,
      ownerId: first.ownerId,
      ownerName: first.ownerName,
      ownerEmail: first.ownerEmail,
      csmId: first.csmId,
      csmName: first.csmName,
      csmEmail: first.csmEmail,
      projectNumbers: first.projectNumbers,
      coacheeName: first.coacheeName,
      sowStartDate: first.sowStartDate,
      sowEndDate: first.sowEndDate,
      contractedHours: first.contractedHours,
      loggedHours: first.loggedHours,
      pctComplete: first.pctComplete,
      lastSessionDate: first.lastSessionDate,
      daysSinceLastSession: first.daysSinceLastSession,
      daysSinceStart: first.daysSinceStart,
      daysUntilEnd: null,
      engagementType: 'Executive Coaching',
      engagementStatus: 'Active',
      deliveringAffiliates: first.deliveringAffiliates,
      riskScore,
      riskTier: riskTierForScore(riskScore),
      riskSignals,
    } satisfies EngagementAssessment;
  }),
  {
    opportunityId: '0061T00000Healthy1',
    opportunityName: 'Elena Torres - Executive Coaching',
    accountName: 'Cisco',
    accountRollupName: null,
    accountDisplayName: 'Cisco',
    ownerId: 'owner-christine-lane',
    ownerName: 'Christine Lane',
    ownerEmail: 'christine.lane@arielexample.com',
    csmId: 'csm-christine-lane',
    csmName: 'Christine Lane',
    csmEmail: 'christine.lane@arielexample.com',
    projectNumbers: ['P-HEALTHY1'],
    coacheeName: 'Elena Torres',
    sowStartDate: '2026-05-01',
    sowEndDate: '2027-05-01',
    contractedHours: 26,
    loggedHours: 8,
    pctComplete: 0.3077,
    lastSessionDate: '2026-08-20',
    daysSinceLastSession: 7,
    daysSinceStart: 118,
    daysUntilEnd: 247,
    engagementType: 'Tier 1 Executive Coaching - 44 Hours',
    engagementStatus: 'Active',
    deliveringAffiliates: [{ name: 'Elena Coach', email: 'elena@arielexample.com' }],
    riskScore: 0,
    riskTier: 'healthy',
    riskSignals: [],
  },
  {
    opportunityId: '0061T00000Complete1',
    opportunityName: 'Completed coaching engagement',
    accountName: 'AbbVie',
    accountRollupName: 'AbbVie',
    accountDisplayName: 'AbbVie',
    ownerId: 'owner-christine-lane',
    ownerName: 'Christine Lane',
    ownerEmail: 'christine.lane@arielexample.com',
    csmId: 'csm-christine-lane',
    csmName: 'Christine Lane',
    csmEmail: 'christine.lane@arielexample.com',
    projectNumbers: ['P-COMPLETE1'],
    coacheeName: 'Former Coachee',
    sowStartDate: '2025-01-01',
    sowEndDate: '2025-12-31',
    contractedHours: 24,
    loggedHours: 24,
    pctComplete: 1,
    lastSessionDate: '2025-12-15',
    daysSinceLastSession: 255,
    daysSinceStart: 604,
    daysUntilEnd: -239,
    engagementType: 'Tier 2 Executive Coaching - 22 Hours',
    engagementStatus: 'Completed',
    deliveringAffiliates: [{ name: 'Former Coach', email: null }],
    riskScore: null,
    riskTier: 'not_scored',
    riskSignals: [],
  },
];

export const ENGAGEMENT_SNAPSHOT: EngagementSnapshot = {
  id: 'mock-snapshot-1',
  generatedAt: '2026-05-20T08:30:00Z',
  opportunitiesScanned: 187,
  counts: { stalledStart: 4, nearCompletion: 3, staleActivity: 4, staleActivity30d: 2 },
};
